import { useEffect, useState } from 'react';
import styled from 'styled-components';

import useTheme from '@rover/kibble/themes/useTheme';

import FloatingFooter from '../../../../components/utils/FloatingFooter';
import { TabletAndDownButton } from '../layouts';

/**
 * We use an ID here because the FrequentlyAskedQuestion component is layered
 * enough that passing a ref down to the underlying html elements would be tedious,
 * and the markup within the FrequentlyAskedQuestions component is very strict
 * so we can't just put an arbitrary div where we want it.
 */
export const BACK_TO_TOP_STARTING_ELEMENT_ID = 'back-to-top-starting-element-id';

export const useBackToTopStartOffset = (): number => {
  const [startOffset, setStartOffset] = useState(0);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') {
      return () => {};
    }

    const backToTopStartingElement = document.getElementById(BACK_TO_TOP_STARTING_ELEMENT_ID);

    const resizeObserver = new ResizeObserver(() => {
      if (backToTopStartingElement) {
        setStartOffset(backToTopStartingElement.offsetTop);
      }
    });

    if (process.env.JS_ENV_CLIENT && backToTopStartingElement) {
      resizeObserver.observe(backToTopStartingElement);
    }

    return (): void => {
      if (process.env.JS_ENV_CLIENT) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  return startOffset;
};

const StyledFloatingFooter = styled(FloatingFooter)`
  background: none !important;
  border: none !important;
  box-shadow: none !important;
  padding-top: 0 !important;
  padding-bottom: 32px !important;
`;

type BackToTopButtonProps = {
  onClick: () => void;
  backToTopStartOffset: number;
};

export const BackToTopButton = ({
  onClick,
  backToTopStartOffset,
}: BackToTopButtonProps): JSX.Element | null => {
  const theme = useTheme();

  return (
    <StyledFloatingFooter>
      <TabletAndDownButton
        // Without the backToTopStartOffset check, the this button is incorrectly visible above
        // the fold from the time between the initial server-side render loads and the relevant
        // useEffect to calculate this value based on teh dom element sizes finishes.
        shouldHide={backToTopStartOffset === 0}
        fullWidth
        onClick={onClick}
        sx={{
          border: `2px solid ${theme.colors.interactive_border.primary_base}`,
          boxShadow: '0px 2px 12px -1px rgba(27, 31, 35, 0.24)',
        }}
      >
        {/* DEV-104195: Translate */}
        Back to top
      </TabletAndDownButton>
    </StyledFloatingFooter>
  );
};
